/**
 * Tokens de espaciado del Design System — FUENTE DE VERDAD.
 *
 * Estos valores se declaran acá una sola vez y quedan disponibles para TODA la app:
 *  - Como utilidades de Tailwind (`p-space200`, `gap-space300`, `m-space500`, `w-space800`, …):
 *    `tailwind.config.js` arma `theme.extend.spacing` a partir de `spacingScale`.
 *  - Como datos (para la página de docs Foundations › Spacing, o cualquier lógica JS):
 *    importando `SPACING_TOKENS`.
 *
 * Para agregar/editar un token, tocá SÓLO este archivo: la tabla de docs y las
 * utilidades de Tailwind se actualizan solas.
 */

export interface SpacingToken {
  /** Nombre del token, ej. 'space200'. Es también la key de la utilidad Tailwind. */
  name: string;
  /** Valor en píxeles. */
  px: number;
}

/**
 * Escala de espaciado, en orden. Nombres y valores tal como están definidos en el
 * style guide de Figma (frame "Spacing", node 8:295).
 *
 * Convención: el sufijo es el % respecto de la base `space100` = 8px
 * (space200 = 16px = 2×, space050 → 4px = 0.5×, etc.). `space9999` es el tope/sentinela.
 */
export const SPACING_TOKENS: SpacingToken[] = [
  { name: 'space0', px: 0 },
  { name: 'space25', px: 2 },
  { name: 'space50', px: 4 },
  { name: 'space75', px: 6 },
  { name: 'space100', px: 8 },
  { name: 'space150', px: 12 },
  { name: 'space200', px: 16 },
  { name: 'space250', px: 20 },
  { name: 'space300', px: 24 },
  { name: 'space400', px: 32 },
  { name: 'space500', px: 40 },
  { name: 'space600', px: 48 },
  { name: 'space800', px: 64 },
  { name: 'space1000', px: 80 },
  { name: 'space1200', px: 96 },
  { name: 'space1400', px: 112 },
  { name: 'space9999', px: 1000 },
];

/**
 * Escala `{ nombre: '<px>px' }` para `theme.extend.spacing` de Tailwind.
 *
 * El reduce dedupe por nombre (conserva la primera aparición) como guarda
 * defensiva: si en el futuro se repitiera un nombre en `SPACING_TOKENS`, no se
 * pisan keys silenciosamente en la escala.
 */
export const spacingScale: Record<string, string> = SPACING_TOKENS.reduce(
  (acc, { name, px }) => {
    if (!(name in acc)) acc[name] = `${px}px`;
    return acc;
  },
  {} as Record<string, string>
);
